import React from 'react';
import dayjs from 'dayjs';
import type { SupplierInvoiceDTO, SupplierInvoiceLineDTO } from '@/services/supplierInvoice.service';
import { formatAmountInWords } from '@/utils/amountInWords';
import { getPartyDisplayLabel } from '@/utils/displayLabels';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { withSpecificationLine } from './LineSpecifications';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE, formatQuantity } from '@/utils/format';
import {
  getInvoicePoOverheads,
  isSupplierInvoiceOverheadLine,
  sumSupplierInvoiceOverheadAmount,
} from '@/utils/supplierInvoiceOverhead';

export interface PrintablePurchaseBillProps {
  invoice: SupplierInvoiceDTO;
  companyName?: string;
  onClose?: () => void;
}

function amt(v: unknown): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

/** Supplier invoice / purchase bill — shared pagination engine with restored fields. */
export const PrintablePurchaseBill: React.FC<PrintablePurchaseBillProps> = ({
  invoice,
  companyName = 'Company',
  onClose,
}) => {
  const lines = invoice.lines ?? [];
  const currency = invoice.currency ?? 'AED';
  const grandTotal = Number(invoice.grand_total ?? 0);
  const overheadTotal = sumSupplierInvoiceOverheadAmount(invoice);
  const goodsSubtotal = Math.round((Number(invoice.subtotal ?? 0) - overheadTotal) * 100) / 100;
  const poOverheads = getInvoicePoOverheads(invoice);
  const poOverheadTotal =
    invoice.po_overhead_total != null
      ? Number(invoice.po_overhead_total)
      : poOverheads.reduce((s, o) => s + (Number(o.amount ?? o.original_amount) || 0), 0);
  const showPoOverheadTable = poOverheads.length > 0;
  const projectHeader = headerProjectLabel(invoice as any, lines as any);
  const supplierLabel = getPartyDisplayLabel(
    invoice.supplier?.supplier_name,
    invoice.supplier?.supplier_code,
    invoice.supplier_id
  );

  const poOverheadsBlock = showPoOverheadTable ? (
    <div style={{ marginTop: 8, fontSize: 12 }}>
      <strong>PO Overheads</strong>
      {overheadTotal <= 0 ? (
        <span style={{ marginLeft: 8 }}>(not cleared on this bill)</span>
      ) : null}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>#</th>
            <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>Type</th>
            <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>
              Description
            </th>
            <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>
              Amount
            </th>
            <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>
              Cleared
            </th>
            <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: '2px 4px' }}>
              Remaining
            </th>
          </tr>
        </thead>
        <tbody>
          {poOverheads.map((oh, idx) => {
            const total = Number(oh.amount ?? oh.original_amount ?? 0) || 0;
            const cleared = Number(oh.invoiced_amount ?? 0) || 0;
            const remaining = Math.round((total - cleared) * 100) / 100;
            return (
              <tr key={oh.id || `oh-${idx}`}>
                <td style={{ padding: '2px 4px' }}>{oh.line_number ?? idx + 1}</td>
                <td style={{ padding: '2px 4px' }}>{oh.overhead_type || '—'}</td>
                <td style={{ padding: '2px 4px' }}>{oh.description || oh.remarks || '—'}</td>
                <td style={{ textAlign: 'right', padding: '2px 4px' }}>{amt(total)}</td>
                <td style={{ textAlign: 'right', padding: '2px 4px' }}>{amt(cleared)}</td>
                <td style={{ textAlign: 'right', padding: '2px 4px' }}>{amt(remaining)}</td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={3} style={{ padding: '4px', fontWeight: 600 }}>
              PO overhead total
            </td>
            <td style={{ textAlign: 'right', padding: '4px', fontWeight: 600 }}>{amt(poOverheadTotal)}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>
  ) : null;

  return (
    <EngineCommercialA4Print
      docType="purchase_bill"
      title="PURCHASE BILL"
      documentNumber={invoice.invoice_number || 'PINV'}
      partyName={supplierLabel}
      onClose={onClose}
      metaLines={[
        { label: 'Company', value: companyName },
        { label: 'Supplier', value: supplierLabel },
        { label: 'Bill No', value: invoice.invoice_number || '' },
        {
          label: 'Bill Date',
          value: invoice.received_date ? dayjs(invoice.received_date).format('DD MMM YYYY') : '',
        },
        { label: 'Supplier Invoice #', value: invoice.supplier_invoice_number || '' },
        {
          label: 'Supplier Invoice Date',
          value: invoice.supplier_invoice_date
            ? dayjs(invoice.supplier_invoice_date).format('DD MMM YYYY')
            : '',
        },
        {
          label: 'Due Date',
          value: invoice.due_date ? dayjs(invoice.due_date).format('DD MMM YYYY') : '',
        },
        { label: 'PO', value: invoice.purchaseOrder?.po_number || '' },
        { label: 'GRN', value: invoice.goodsReceiptNote?.grn_number || '' },
        { label: 'Project', value: projectHeader !== '-' ? projectHeader : '' },
        {
          label: 'PO Overhead Total',
          value: showPoOverheadTable ? `${amt(poOverheadTotal)} ${currency}` : '',
        },
        {
          label: 'Overheads on bill',
          value: overheadTotal > 0 ? `${amt(overheadTotal)} ${currency}` : '',
        },
        {
          label: 'Accounts Payable',
          value: invoice.debitAccount
            ? `${invoice.debitAccount.account_code} - ${invoice.debitAccount.account_name}`
            : '',
        },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'code', label: 'Item' },
        { key: 'description', label: 'Description' },
        { key: 'project', label: 'Project' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'rate', label: 'Rate', align: 'right' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ]}
      lines={lines.map((line: SupplierInvoiceLineDTO) => {
        const overhead = isSupplierInvoiceOverheadLine(line);
        const desc =
          formatItemDescriptionWithProfile(line.item_description, line).trim() || '—';
        return {
          lineNumber: line.line_number,
          description: overhead
            ? !/^overhead/i.test(desc) ? `Overhead — ${desc}` : desc
            : withSpecificationLine(desc, line.specifications),
          cells: {
            code: overhead ? 'OVERHEAD' : line.item_code || '—',
            project: projectLabel(line),
            qty: formatQuantity(line.quantity_billed, { maximumFractionDigits: 3 }),
            rate: amt(line.unit_price),
            amount: amt(line.amount),
          },
        };
      })}
      finals={[
        ...(showPoOverheadTable
          ? [{ id: 'poOverheads', heightPx: 56 + poOverheads.length * 18 }]
          : []),
        { id: 'totals', heightPx: overheadTotal > 0 || showPoOverheadTable ? 160 : 130 },
        ...(invoice.notes ? [{ id: 'notes', heightPx: 48 }] : []),
        { id: 'sign', heightPx: 56 },
      ]}
      finalBlocks={{
        poOverheads: poOverheadsBlock,
        totals: (
          <div>
            <p>
              <strong>Amount in words:</strong> {formatAmountInWords(grandTotal, currency)}
            </p>
            <table style={{ marginLeft: 'auto', fontSize: 12 }}>
              <tbody>
                <tr>
                  <td>Subtotal (goods)</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>{amt(goodsSubtotal)}</td>
                </tr>
                {overheadTotal > 0 ? (
                  <tr>
                    <td>Overheads (on this bill)</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>{amt(overheadTotal)}</td>
                  </tr>
                ) : showPoOverheadTable ? (
                  <tr>
                    <td>PO Overheads (not on this bill)</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>{amt(poOverheadTotal)}</td>
                  </tr>
                ) : null}
                <tr>
                  <td>Subtotal</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>{amt(invoice.subtotal)}</td>
                </tr>
                {Number(invoice.discount_amount) > 0 ? (
                  <tr>
                    <td>Discount</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                      {amt(invoice.discount_amount)}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <td>Total after discount</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    {amt(invoice.total_after_discount)}
                  </td>
                </tr>
                {Number(invoice.vat_amount) > 0 ? (
                  <tr>
                    <td>VAT</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>{amt(invoice.vat_amount)}</td>
                  </tr>
                ) : null}
                {Number(invoice.freight_charges) + Number(invoice.other_charges) > 0 ? (
                  <tr>
                    <td>Charges</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                      {amt(Number(invoice.freight_charges) + Number(invoice.other_charges))}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <td>
                    <strong>Grand Total</strong>
                  </td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    <strong>
                      {amt(grandTotal)} {currency}
                    </strong>
                  </td>
                </tr>
                {Number(invoice.paid_amount ?? 0) > 0 ? (
                  <tr>
                    <td>Paid Amount</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                      {amt(invoice.paid_amount)}
                    </td>
                  </tr>
                ) : null}
                {Number(invoice.debit_note_adjusted_amount ?? 0) > 0 ? (
                  <tr>
                    <td>Debit Note Adjusted</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                      {amt(invoice.debit_note_adjusted_amount)}
                    </td>
                  </tr>
                ) : null}
                {Number(invoice.advance_adjusted_amount ?? 0) > 0 ? (
                  <tr>
                    <td>Advance Adjusted</td>
                    <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                      {amt(invoice.advance_adjusted_amount)}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <td>
                    <strong>Balance Due</strong>
                  </td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    <strong>
                      {amt(invoice.balance_due ?? 0)} {currency}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
        notes: invoice.notes ? (
          <div>
            <strong>Notes</strong>
            <p>{invoice.notes}</p>
          </div>
        ) : null,
        sign: (
          <div style={{ display: 'flex', gap: 48, marginTop: 8 }}>
            <div>
              Prepared by
              <br />
              _______________________
            </div>
            <div>
              Approved by
              <br />
              _______________________
            </div>
          </div>
        ),
      }}
    />
  );
};
